import type { AdminGame, DxMode, GamescopeSettings, LinuxSettings } from '@mcgames/contracts';

export const DX_MODES: readonly DxMode[] = ['dxvk_vkd3d', 'dxvk_only', 'wined3d'];

/** A setting that can be left to the lower layers (`unset`) or forced on or off. */
export type TriState = 'unset' | 'on' | 'off';

export interface EnvRow {
  /** Stable identity for React keys across removals. */
  key: number;
  name: string;
  value: string;
}

/** Gamescope as the form edits it; the numbers stay text until they are saved. */
export interface GamescopeDraft {
  enabled: boolean;
  outputWidth: string;
  outputHeight: string;
  internalWidth: string;
  internalHeight: string;
  fpsLimit: string;
  fsr: boolean;
  fullscreen: boolean;
}

export interface LinuxDraft {
  protonVersion: string;
  dxMode: DxMode | 'unset';
  env: EnvRow[];
  mangohud: TriState;
  gamemode: TriState;
  /** `null` is "not set": the key is left out of the saved settings. */
  gamescope: GamescopeDraft | null;
  launchArgs: string;
}

export const GAMESCOPE_NUMBER_FIELDS = [
  'outputWidth',
  'outputHeight',
  'internalWidth',
  'internalHeight',
  'fpsLimit',
] as const satisfies readonly (keyof GamescopeDraft)[];

let nextKey = 1;

export function newEnvRow(name = '', value = ''): EnvRow {
  return { key: nextKey++, name, value };
}

export function newGamescopeDraft(): GamescopeDraft {
  return {
    enabled: true,
    outputWidth: '',
    outputHeight: '',
    internalWidth: '',
    internalHeight: '',
    fpsLimit: '',
    fsr: false,
    fullscreen: false,
  };
}

function toTri(value: boolean | undefined): TriState {
  if (value === undefined) return 'unset';
  return value ? 'on' : 'off';
}

function fromTri(value: TriState): boolean | undefined {
  if (value === 'unset') return undefined;
  return value === 'on';
}

function numberText(value: number | null): string {
  return value === null ? '' : value.toString();
}

function toNumber(text: string): number | null {
  return text.trim() === '' ? null : Number(text);
}

function gamescopeToDraft(settings: GamescopeSettings): GamescopeDraft {
  return {
    enabled: settings.enabled,
    outputWidth: numberText(settings.outputWidth),
    outputHeight: numberText(settings.outputHeight),
    internalWidth: numberText(settings.internalWidth),
    internalHeight: numberText(settings.internalHeight),
    fpsLimit: numberText(settings.fpsLimit),
    fsr: settings.fsr,
    fullscreen: settings.fullscreen,
  };
}

export function linuxDraftFromGame(game: AdminGame): LinuxDraft {
  const settings = game.linuxDefaults;
  return {
    protonVersion: settings.protonVersion ?? '',
    dxMode: settings.dxMode ?? 'unset',
    env: Object.entries(settings.env ?? {}).map(([name, value]) => newEnvRow(name, value)),
    mangohud: toTri(settings.mangohud),
    gamemode: toTri(settings.gamemode),
    gamescope: settings.gamescope === undefined ? null : gamescopeToDraft(settings.gamescope),
    launchArgs: settings.launchArgs ?? '',
  };
}

/** Names used by more than one row, after trimming; rows without a name are ignored. */
export function duplicateEnvNames(env: EnvRow[]): Set<string> {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const row of env) {
    const name = row.name.trim();
    if (name === '') continue;
    if (seen.has(name)) duplicates.add(name);
    seen.add(name);
  }
  return duplicates;
}

/** Only what is set: an unset setting has no key at all. */
export function linuxSettingsFromDraft(draft: LinuxDraft): LinuxSettings {
  const settings: LinuxSettings = {};
  const proton = draft.protonVersion.trim();
  if (proton !== '') settings.protonVersion = proton;
  if (draft.dxMode !== 'unset') settings.dxMode = draft.dxMode;
  const env: Record<string, string> = {};
  for (const row of draft.env) {
    const name = row.name.trim();
    if (name !== '') env[name] = row.value;
  }
  if (Object.keys(env).length > 0) settings.env = env;
  const mangohud = fromTri(draft.mangohud);
  if (mangohud !== undefined) settings.mangohud = mangohud;
  if (draft.gamescope !== null) {
    settings.gamescope = {
      enabled: draft.gamescope.enabled,
      outputWidth: toNumber(draft.gamescope.outputWidth),
      outputHeight: toNumber(draft.gamescope.outputHeight),
      internalWidth: toNumber(draft.gamescope.internalWidth),
      internalHeight: toNumber(draft.gamescope.internalHeight),
      fpsLimit: toNumber(draft.gamescope.fpsLimit),
      fsr: draft.gamescope.fsr,
      fullscreen: draft.gamescope.fullscreen,
    };
  }
  const gamemode = fromTri(draft.gamemode);
  if (gamemode !== undefined) settings.gamemode = gamemode;
  const launchArgs = draft.launchArgs.trim();
  if (launchArgs !== '') settings.launchArgs = launchArgs;
  return settings;
}
